// context/OrgContext.tsx
import React, { createContext, useContext, useMemo, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "./AuthContext";

interface OrgContextType {
  orgId: string;
  mode: "adminAuth" | "adminUrl" | "clientAuth";
  navigateOrg: (path: string) => void;
  navigateOrgUrl: (path: string) => string;
}

const OrgContext = createContext<OrgContextType | undefined>(undefined);

interface OrgProviderProps {
  children: React.ReactNode;
  mode: "adminAuth" | "adminUrl" | "clientAuth";
}

export const OrgProvider: React.FC<OrgProviderProps> = ({
  children,
  mode: explicitMode,
}) => {
  const { orgId: urlOrgId } = useParams<{ orgId: string }>();
  const navigate = useNavigate();
  const { currentUser } = useAuth();

  // Determine orgId based on mode
  const { orgId } = useMemo(() => {
    switch (explicitMode) {
      case "adminUrl": {
        // Get orgId from URL parameter
        if (!urlOrgId) {
          throw new Error("Organization ID not found in URL for adminUrl mode");
        }
        return { orgId: urlOrgId };
      }

      case "adminAuth":
      case "clientAuth": {
        // Get orgId from user's first organization
        const authOrgId = currentUser?.organizations[0]?.id;
        if (!authOrgId) {
          throw new Error(
            `Organization ID not available in user context for ${explicitMode} mode`
          );
        }
        return { orgId: authOrgId };
      }

      default: {
        // Type guard to ensure exhaustiveness
        const _exhaustive: never = explicitMode;
        throw new Error(`Unknown mode: ${_exhaustive}`);
      }
    }
  }, [explicitMode, urlOrgId, currentUser]);

  // Helper function for URL construction
  const buildOrgUrl = useCallback(
    (path: string): string => {
      // Normalize path to always start with /
      const normalizedPath = path.startsWith("/") ? path : `/${path}`;

      switch (explicitMode) {
        case "adminUrl":
        case "adminAuth":
          // Both admin modes use /organizations/{orgId}/* pattern
          return `/organizations/${orgId}${normalizedPath}`;

        case "clientAuth":
          // Client mode uses /portal/* pattern
          return `/portal${normalizedPath}`;

        default:
          const _exhaustive: never = explicitMode;
          throw new Error(`Unknown mode: ${_exhaustive}`);
      }
    },
    [explicitMode, orgId]
  );

  const navigateOrg = useCallback(
    (path: string) => {
      const url = buildOrgUrl(path);
      navigate(url);
    },
    [buildOrgUrl, navigate]
  );

  const navigateOrgUrl = useCallback(
    (path: string) => {
      return buildOrgUrl(path);
    },
    [buildOrgUrl]
  );

  return (
    <OrgContext.Provider
      value={{ orgId, mode: explicitMode, navigateOrg, navigateOrgUrl }}
    >
      {children}
    </OrgContext.Provider>
  );
};

export const useOrg = () => {
  const context = useContext(OrgContext);
  if (!context) {
    throw new Error("useOrg must be used within an OrgProvider");
  }
  return context;
};
